import { formatUnit } from "./formatUnit";

export const isNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

export const normalizeZScore = (rawZScore) => {
  if (rawZScore == null) return null;

  if (typeof rawZScore === "number") {
    return rawZScore;
  }

  if (typeof rawZScore === "object") {
    if (isNumber(rawZScore.value)) return rawZScore.value;
    if (isNumber(rawZScore.zScore)) return rawZScore.zScore;
  }

  const parsed = Number(rawZScore);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeZScoreColor = (rawZScore) => {
  if (!rawZScore) return null;

  if (typeof rawZScore === "string") {
    return rawZScore.toUpperCase();
  }

  if (typeof rawZScore === "object") {
    if (typeof rawZScore.color === "string") {
      return rawZScore.color.toUpperCase();
    }

    if (typeof rawZScore?.color?.j2_1 === "string") {
      return rawZScore.color.j2_1.toUpperCase();
    }
    if (typeof rawZScore?.color?.i2_1 === "string") {
      return rawZScore.color.i2_1.toUpperCase();
    }
    if (typeof rawZScore?.color?.k2_1 === "string") {
      return rawZScore.color.k2_1.toUpperCase();
    }
  }

  return null;
};

export const aggregateIndicatorMetrics = (videos = []) => {
  const accumulator = {};

  videos.forEach((video) => {
    const analysis = video?.analysis;
    if (!analysis) return;

    Object.entries(analysis).forEach(([indicatorId, indicatorData = {}]) => {
      const value = indicatorData?.indicatorValue ?? indicatorData?.value;
      const zScoreValue = normalizeZScore(indicatorData?.zScore);
      const zScoreColor = normalizeZScoreColor(indicatorData?.zScore);
      const unit = indicatorData?.unit ?? null;

      if (!accumulator[indicatorId]) {
        accumulator[indicatorId] = {
          unit,
          values: [],
          zScores: [],
          zScoreEntries: [],
        };
      }

      const bucket = accumulator[indicatorId];

      if (isNumber(value)) {
        bucket.values.push(value);
      }

      if (isNumber(zScoreValue)) {
        bucket.zScores.push(zScoreValue);
        bucket.zScoreEntries.push({ value: zScoreValue, color: zScoreColor });
      }

      if (!bucket.unit && unit) {
        bucket.unit = unit;
      }
    });
  });

  return Object.entries(accumulator).reduce((acc, [indicatorId, bucket]) => {
    const { values, zScores, zScoreEntries, unit } = bucket;

    if (!values.length && !zScores.length) {
      acc[indicatorId] = { unit: unit ?? null };
      return acc;
    }

    const mean = values.length
      ? values.reduce((sum, v) => sum + v, 0) / values.length
      : null;

    const variance = values.length
      ? values.reduce((sum, v) => sum + Math.pow(v - (mean ?? 0), 2), 0) /
        (values.length || 1)
      : null;

    const stdDev = variance == null ? null : Math.sqrt(variance);

    const zScoreAverage = zScores.length
      ? zScores.reduce((sum, v) => sum + v, 0) / zScores.length
      : null;

    let zScoreColor = null;

    if (zScoreEntries.length) {
      const sortedEntries = [...zScoreEntries].sort(
        (a, b) => Math.abs(b?.value ?? 0) - Math.abs(a?.value ?? 0),
      );
      zScoreColor = sortedEntries[0]?.color ?? null;
    }

    acc[indicatorId] = {
      unit: unit ?? null,
      avg: mean,
      stdDev: stdDev != null ? stdDev : null,
      zScore: zScoreAverage,
      zScoreColor,
      count: values.length,
    };

    return acc;
  }, {});
};

export const summarizeIndicatorsByClub = (
  videoArray,
  selectedTableData,
  tourReference = {},
) => {
  const clubMap = {
    D: "Driver",
    default: "Iron",
  };

  const videosToAnalyse =
    selectedTableData && selectedTableData?.length > 0
      ? selectedTableData
      : videoArray && videoArray?.length > 0
        ? videoArray
        : [];

  const clubVideos = {
    Driver: [],
    Iron: [],
  };

  videosToAnalyse.forEach((video) => {
    const clubType = clubMap[video.userData?.golfClub] || "Iron";
    clubVideos[clubType].push(video);
  });

  const getIndicatorRange = (clubType, indicatorId) => {
    const source =
      clubType === "Driver" ? tourReference.driver : tourReference.iron;
    return (
      source?.find((d) => d.indicatorId === indicatorId)?.indicatorRange ?? null
    );
  };

  const buildClubSummary = (clubType) => {
    const aggregatedMetrics = aggregateIndicatorMetrics(clubVideos[clubType]);

    const tourIndicators =
      clubType === "Driver" ? tourReference.driver : tourReference.iron;
    const tourIndicatorIds =
      tourIndicators?.map((indicator) => indicator.indicatorId) ?? [];

    const allIndicatorIds = new Set([
      ...Object.keys(aggregatedMetrics),
      ...tourIndicatorIds,
    ]);

    return Array.from(allIndicatorIds).reduce((acc, indicatorId) => {
      const baseMetric = aggregatedMetrics[indicatorId] ?? EMPTY_METRIC;
      const indicatorRange = getIndicatorRange(clubType, indicatorId);

      acc[indicatorId] = {
        ...baseMetric,
        tourAvg: isNumber(baseMetric.avg)
          ? baseMetric.avg - (indicatorRange ?? 0)
          : null,
      };

      return acc;
    }, {});
  };

  return {
    Driver: buildClubSummary("Driver"),
    Iron: buildClubSummary("Iron"),
  };
};

const COLOR_CLASS_BY_LABEL = {
  GREEN: "text-success600",
  ORANGE: "text-warning600",
  RED: "text-error600",
};

export const getZScoreColorClass = (metric) => {
  const label = metric?.zScoreColor;
  if (label && COLOR_CLASS_BY_LABEL[label]) {
    return COLOR_CLASS_BY_LABEL[label];
  }

  const value = metric?.zScore;
  if (!isNumber(value)) return "text-gray600";

  const absVal = Math.abs(value);
  if (absVal < 0.5) return COLOR_CLASS_BY_LABEL["GREEN"];
  if (absVal < 1.5) return COLOR_CLASS_BY_LABEL["ORANGE"];
  return COLOR_CLASS_BY_LABEL["RED"];
};

export function getDeviationColorClass(metric) {
  const valueDeviation = metric?.tourAvg;
  const valueStdDev = metric?.stdDev;

  // if (!isNumber(valueDeviation) || !isNumber(valueStdDev))
  return "text-gray600";

  const absDeviation = Math.abs(valueDeviation);
  const absStdDev = Math.abs(valueStdDev);

  if (absDeviation < absStdDev) return COLOR_CLASS_BY_LABEL["GREEN"]; // green
  if (absDeviation < 1.5 * absStdDev) return COLOR_CLASS_BY_LABEL["ORANGE"]; // orange
  return COLOR_CLASS_BY_LABEL["RED"]; // red
}

export const EMPTY_METRIC = {
  avg: null,
  stdDev: null,
  unit: null,
  zScore: null,
  zScoreColor: null,
  tourAvg: null,
};

export const buildMetric = (metricsMap, indicatorId) => {
  if (!metricsMap) return EMPTY_METRIC;

  return {
    ...EMPTY_METRIC,
    ...(metricsMap?.[indicatorId] ?? {}),
  };
};

export const formatPrimaryValue = (metric, fallbackUnit, options = {}) => {
  if (!isNumber(metric?.avg)) return "n/a";

  const unit = metric?.unit || fallbackUnit;
  return formatUnit(unit, metric.avg, {
    convert: true,
    ...options,
  });
};

export const formatStdDeviation = (metric, fallbackUnit, options = {}) => {
  if (!isNumber(metric?.stdDev)) return "n/a";

  const { dp: requestedDp, showSymbol, ...restOptions } = options;
  const unit = metric?.unit || fallbackUnit;
  return formatUnit(unit, metric.stdDev, {
    convert: true,
    ...restOptions,
    dp: typeof requestedDp === "number" ? requestedDp : 2,
    showSymbol: showSymbol ?? false,
  });
};

export const formatZScore = (metric, options = {}) => {
  if (!isNumber(metric?.zScore)) return "n/a";

  const resolvedDp =
    typeof options?.dp === "number"
      ? options.dp
      : Math.abs(metric.zScore) < 1
        ? 1
        : 0;

  return formatUnit("", metric.zScore, {
    convert: false,
    dp: resolvedDp,
    ...options,
  });
};

export const formatTourDeviation = (metric, fallbackUnit, options = {}) => {
  if (!isNumber(metric?.tourAvg)) return "n/a";

  const unit = metric?.unit || fallbackUnit;
  const { dp: requestedDp, ...restOptions } = options;

  return formatUnit(unit, metric.tourAvg, {
    convert: true,
    ...restOptions,
    ...(typeof requestedDp === "number" ? { dp: requestedDp } : {}),
    referenceValue: metric?.avg,
  });
};
